import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck, Mail, Phone, Globe } from 'lucide-react';

const PHONE_DISPLAY = '(305) 432-3557';
const PHONE_TEL = '+13054323557';

const Section = ({ title, children }) => (
  <div className="space-y-3">
    <h2 className="text-xl md:text-2xl font-bold text-slate-900">{title}</h2>
    <div className="text-slate-600 leading-relaxed space-y-3 text-base">{children}</div>
  </div>
);

export const PrivacyPolicyPage = () => {
  useEffect(() => {
    document.title = 'Privacy Policy | Calyvra Insurance';
  }, []);

  return (
    <div className="overflow-hidden" data-testid="privacy-policy-page">
      <section className="gradient-hero py-16 lg:py-24">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-sky-100 rounded-full mb-6">
              <ShieldCheck className="h-4 w-4 text-sky-600" />
              <span className="text-sm font-medium text-sky-700">Your Privacy Matters</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Privacy Policy</h1>
            <p className="text-slate-600 text-lg">
              Calyvra Insurance Agency is committed to protecting your privacy. This policy explains how we
              collect, use, share, and protect your personal information.
            </p>
            <p className="text-sm text-slate-400 mt-4">Last updated: June 2026</p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto bg-white rounded-2xl border border-slate-100 shadow-sm p-8 md:p-12 space-y-10">

            <Section title="Information We Collect">
              <p>
                We collect personal information that you voluntarily provide to us when you request a quote,
                contact us, enroll in coverage, or communicate with our team. This may include your name, email
                address, phone number, ZIP code, preferred language, coverage interests, and any details you
                include in your messages. When required to process insurance applications, we may also collect
                additional eligibility information necessary to serve you.
              </p>
            </Section>

            <Section title="How We Use Your Information">
              <p>We use the information we collect to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Respond to your inquiries and provide requested quotes and services.</li>
                <li>Assist with health, dental, vision, Medicare, and ACA enrollment.</li>
                <li>Send appointment reminders, policy updates, and customer support communications.</li>
                <li>Administer Premium Assistance Program communications where applicable.</li>
                <li>Improve our services and comply with legal and regulatory obligations.</li>
              </ul>
            </Section>

            <Section title="How We Share Your Information">
              <p>
                We may share your information with licensed insurance carriers and partners strictly as needed
                to process your application, obtain quotes, or provide the services you request. We may also
                disclose information when required by law or to protect our legal rights. We do not sell your
                personal information.
              </p>
            </Section>

            <Section title="SMS Privacy">
              <p className="font-semibold text-slate-800">
                SMS consent is not shared with third parties or affiliates for marketing purposes.
              </p>
              <p>
                If you opt in to receive text messages, your mobile information is used solely to deliver the
                messages you requested (such as insurance inquiries, applications, appointments, policy updates,
                and customer support). You can opt out at any time by replying STOP, or reply HELP for
                assistance. Message frequency may vary, and message and data rates may apply.
              </p>
            </Section>

            <Section title="How We Protect Your Information">
              <p>
                We implement reasonable administrative, technical, and physical safeguards designed to protect
                your personal information against unauthorized access, disclosure, alteration, or destruction.
                While no method of transmission or storage is 100% secure, we continually work to protect your
                information.
              </p>
            </Section>

            <Section title="Accessing, Updating, or Deleting Your Information">
              <p>
                You may request access to, correction of, or deletion of the personal information we hold about
                you at any time. To make a request, contact us at{' '}
                <a href="mailto:info@calyvrainsurance.com" className="text-sky-600 hover:underline">info@calyvrainsurance.com</a>{' '}
                or call <a href={`tel:${PHONE_TEL}`} className="text-sky-600 hover:underline">{PHONE_DISPLAY}</a>.
                We will respond to your request in accordance with applicable law.
              </p>
            </Section>

            <Section title="Cookies">
              <p>
                Our website uses cookies and similar technologies to operate the site, understand how visitors
                use it, and improve your experience. We may use analytics tools (such as Google Analytics via
                Google Tag Manager) that set cookies to measure site traffic. You can control or disable cookies
                through your browser settings; however, some features of the site may not function properly if
                cookies are disabled.
              </p>
            </Section>

            <Section title="Contact Information">
              <div className="rounded-xl bg-slate-900 text-white p-6 space-y-3">
                <p className="text-slate-300">If you have questions about this Privacy Policy, contact us:</p>
                <a href={`tel:${PHONE_TEL}`} className="flex items-center gap-3 text-slate-200 hover:text-white transition-colors">
                  <Phone className="h-5 w-5 text-sky-400" /> {PHONE_DISPLAY}
                </a>
                <a href="mailto:info@calyvrainsurance.com" className="flex items-center gap-3 text-slate-200 hover:text-white transition-colors">
                  <Mail className="h-5 w-5 text-sky-400" /> info@calyvrainsurance.com
                </a>
                <a href="https://calyvrainsurance.com" className="flex items-center gap-3 text-slate-200 hover:text-white transition-colors">
                  <Globe className="h-5 w-5 text-sky-400" /> https://calyvrainsurance.com
                </a>
              </div>
            </Section>

            <div className="pt-4 border-t border-slate-100">
              <p className="text-sm text-slate-500">
                See also our{' '}
                <Link to="/sms-terms" className="text-sky-600 hover:underline font-medium">SMS Terms &amp; Conditions</Link>.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
